import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

export const homeFor = (role) => (role === "platform_admin" ? "/platform" : "/app");

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      nav(homeFor(data.user.role));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-8 sm:px-16 py-12">
        <Brand className="mb-12" />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-sm w-full">
          <p className="overline text-accent mb-3">Welcome back</p>
          <h1 className="text-4xl font-light tracking-tighter mb-8">Sign in to your workspace</h1>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-sm font-medium">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 w-full border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background" />
            </div>
            <div>
              <label className="text-sm font-medium">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 w-full border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background" />
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-3 hover:bg-accent transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">
            New here? <Link to="/signup" className="text-accent font-medium hover:underline" data-testid="login-to-signup-link">Create an organization →</Link>
          </p>
        </motion.div>
      </div>
      <div className="hidden lg:block relative bg-primary noise-bg">
        <div className="absolute inset-0 grid-lines opacity-20" aria-hidden="true" />
        <div className="relative h-full flex flex-col justify-center px-16 text-primary-foreground">
          <h2 className="text-5xl font-light tracking-tighter leading-tight">Onboard every client<br /><span className="font-black">with precision.</span></h2>
          <p className="mt-6 text-primary-foreground/70 max-w-md text-lg">Documents, signatures and approvals — structured, gated and beautifully simple.</p>
        </div>
      </div>
    </div>
  );
}
